// src/components/tutor/PlanCheckGroup.tsx
//
// The plan-check part of a Sage reply (see plan-check-layout.ts for the rule):
//   - PlanFitCard: the reply's final check_plan_time, when it fits — a result
//     card that sits right under the plan text and says, in words, that the
//     plan fits the student's daily time.
//   - SupersededChecks: earlier checks that didn't fit, collapsed into a
//     native <details> ("2 earlier drafts didn't fit — show"). Keyboard and
//     screen-reader operable with no JS; the drafts stay one click away for a
//     student who wants to see what Sage left out and why. The same disclosure
//     holds a failed check the reply moved on from to a Start card
//     ("Didn't fit — options below"), passed in as `label`.

'use client'

import { CalendarCheck } from 'lucide-react'
import type { SageAction } from '@unlimitedtests/core'
import { planFitDetail, planFitHeadline } from '@/lib/tutor-tools/plan-check-summary'
import { setAsideLabel, supersededLabel } from '@/lib/tutor-tools/plan-check-layout'
import ToolCard, { type ToolCardCall, type ToolCardResult } from './ToolCard'

type FitResult = Parameters<typeof planFitHeadline>[0]

export function PlanFitCard({ result }: { result: ToolCardResult }) {
  const r = (result.result ?? {}) as FitResult
  return (
    <section
      aria-label="Plan time check"
      className="my-1 flex max-w-[85%] items-start gap-3 rounded-xl border border-emerald-600/40 bg-emerald-50 px-3.5 py-2.5 text-emerald-950 dark:border-emerald-400/30 dark:bg-emerald-950/30 dark:text-emerald-50"
    >
      <CalendarCheck
        aria-hidden="true"
        className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-300"
      />
      <div className="min-w-0">
        <p className="text-sm font-semibold">{planFitHeadline(r)}</p>
        <p className="mt-0.5 text-xs text-emerald-900/80 dark:text-emerald-100/80">
          {planFitDetail(r)}
        </p>
      </div>
    </section>
  )
}

export function SupersededChecks({
  calls,
  resultFor,
  messageId,
  onLaunch,
  variant = 'superseded',
}: {
  calls: ToolCardCall[]
  resultFor: (call: ToolCardCall) => ToolCardResult | undefined
  messageId: string
  onLaunch?: (action: SageAction) => void
  /** 'setAside': failed checks the reply moved on from to a Start card. */
  variant?: 'superseded' | 'setAside'
}) {
  if (calls.length === 0) return null
  return (
    <details className="group text-xs text-muted-foreground">
      <summary className="cursor-pointer select-none rounded py-0.5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {variant === 'setAside'
          ? setAsideLabel(calls, resultFor)
          : supersededLabel(calls, resultFor)}
        <span aria-hidden="true">
          {' — '}
          <span className="underline group-open:hidden">show</span>
          <span className="hidden underline group-open:inline">hide</span>
        </span>
      </summary>
      <div className="mt-1 space-y-1 border-l-2 border-border pl-3">
        {calls.map((call) => (
          <ToolCard
            key={call.id}
            call={call}
            result={resultFor(call)}
            messageId={messageId}
            onLaunch={onLaunch}
          />
        ))}
      </div>
    </details>
  )
}
